<template>
  <v-card
    class="treatment-card"
    :class="[ bgColor, {'unSelectedByDoctor': isDisabled && !!filter} ]"
    :disabled="disabled"
    @mousedown="start"
    @mouseleave="stop"
    @mouseup="stop"
    @touchcancel="stop"
    @touchend="stop"
    @touchstart="start"
  >
    <div
      class="tc-initials absolute top-0 right-0 w-8 h-5 rounded-none rounded-tr-md"
    >
      <div class="comment-icon">
        <v-icon
            v-if="treatment.has_notes"
            color="blue-darken-2"
            :size="18">mdi-message-text</v-icon>
      </div>
      <div
        class="text-xs font-bold text-center"
        :style="{
          backgroundColor: initialsBackgroundColor,
          color: $filters.foregroundColor(initialsBackgroundColor)
        }"
      >
        {{ initials }}
      </div>
    </div>
    <div class="text-body-2">
      {{ title }}
    </div>

    <div v-if="isCompleted" class="flex justify-center">
      <v-btn
          density="compact"
          variant="text"
          icon="mdi-checkbox-marked-circle-outline"
          :color="`#01492b`"
      />
    </div>

    <div v-else-if="!startedAt || isPaused" class="flex justify-center">
      <v-btn
          id="btn-1"
        density="compact"
        color="gray-darken-2"
        variant="text"
        :icon="isPaused ? 'mdi-motion-play-outline' : 'mdi-play'"
        :disabled="isDisabled"
        v-if="interactWithBoard"
        @click.stop="handlePlay"
        @mousedown.stop
        @mouseup.stop
        @touchstart.stop
        @touchend.stop
      />
    </div>

    <div v-else class="text-body-2 treatment-time">
      {{ timeElapsedFormatted }}{{ timeElapsed >= 3600000 ? 'm' : 's'}} | {{ timeAllocatedFormatted }}{{ timeAllocated >= 3600000 ? 'm' : 's'}}
    </div>

    <TreatmentDialog
      v-if="openedTreatment === treatment.uuid"
      :appointment="appointment"
      :appointment-id="appointment.uuid"
      :name="treatment.treatment.full_name"
      :administrator="treatment.doctor.uuid"
      :notes="treatmentNotes"
      :room-name="room.name"
      :time="timeElapsed"
      :allocated-time="timeAllocated"
      :treatment="treatment"
      :card-state="treatmentState"
      :is-disabled="isDisabled"
      @close="closeDialog"
      @play="handlePlay"
      @pause="handlePause"
      @complete-treatment="handleCompleteTreatment"
      @reset-treatment="handleResetTreatment"
      @update-allocated-time="handleUpdatetimeAllocated"
    />
  </v-card>
</template>

<script lang="ts">
import TreatmentDialog from '@/components/modules/TreatmentDialog.vue';
import {mdiCheckboxMarkedCircleOutline, mdiMotionPlayOutline, mdiPlay} from '@mdi/js';
import { defineComponent } from 'vue';
import {mapActions, mapState} from 'vuex';
import randomColor from 'randomcolor';
import store from '@/store';

declare interface BaseData {
  checkIcon: any;
  showDialog: boolean;
  interval: number | undefined;
  count: number;
  interactWithBoard: boolean;
  localAllocatedTime: number | null;
  allocatedTimeDebounceTimer: ReturnType<typeof setTimeout> | null;
  clientResumedAt: number | null;
}

export default defineComponent({
  name: 'TreatmentCard',
  components: { TreatmentDialog },
  props: {
    id: { type: String, required: true },
    appointment: { type: Object, required: true },
    treatment: { type: Object, required: true },
    room: { type: Object, required: true },
    appointmentId: { type: String, required: true },
    initials: { type: String, required: true },
    initialsColor: { type: String, required: false },
    roomId: {type: [String, Number], required: true},
    title: { type: String, required: true },
    initialAllocatedTime: { type: Number, required: true },
    startedAt: {
      validator: (prop) => typeof prop === 'string' ||  prop === null,
      required: true,
    },
    endedAt: {
      validator: (prop) => typeof prop === 'string' ||  prop === null,
      required: true,
    },
    disabled: { type: Boolean, default: false },
  },
  data(): BaseData {
    return {
      checkIcon: mdiCheckboxMarkedCircleOutline,
      showDialog: false,
      interval: undefined,
      count: 0,
      interactWithBoard: false,
      localAllocatedTime: null as number | null,
      allocatedTimeDebounceTimer: null as ReturnType<typeof setTimeout> | null,
      clientResumedAt: null as number | null,
    };
  },
  async mounted() {
    this.interactWithBoard = await this.$store.dispatch('ableUserTo', 'interact with board');
  },
  beforeUnmount() {
    if (this.allocatedTimeDebounceTimer) {
      clearTimeout(this.allocatedTimeDebounceTimer);
    }
  },
  computed: {
    ...mapState({
      timeNow: ({ time: x }) => x,
      openedTreatment: ({ openedTreatment }) => openedTreatment,
      serverClockOffset: ({ auth }) => auth.serverClockOffset ?? 0,
    }),
    canInteractWithTreatment() {
      return !['completed'].includes(this.treatment.status);
    },
    treatmentNotes() {
      return this.appointment.patient.notes ? this.appointment.patient.notes.filter((note) => note.treatment.uuid === this.treatment.treatment.uuid) : [];
    },
    bgColor(): string {
      if (!!this.endedAt || !this.startedAt) { return 'whiteCard'; }
      if (this.isOverTime) { return 'redCard'; }
      if (this.isOverTimeAlmost) { return 'yellowCard'; }
      return 'greenCard';
    },
    treatmentState(): string {
      if (!!this.endedAt) { return 'completed'; }
      if (!this.startedAt || this.isPaused) { return 'default'; }
      if (this.isOverTime) { return 'overtime'; }
      if (this.isOverTimeAlmost) { return 'warning'; }
      return 'active';
    },
    isOngoing() { return !this.endedAt && !!this.startedAt; },
    isOverTime() {
      return this.timeElapsed > this.timeAllocated;
    },
    isOverTimeAlmost() {
      return this.timeElapsed > (this.timeAllocated - 180000);
    },
    isPaused(): boolean { return this.treatment.status === 'paused'; },
    isProcessing(): boolean { return this.treatment.status === 'processing'; },
    isCompleted() {
      return ['completed'].includes(this.treatment.status);
    },
    timeElapsedBefore(): number { return this.treatment.time_elapsed * 1000; },
    timeElapsed(): number {
      return Math.max(0, this.timeElapsedBefore + (this.isPaused ? 0 : this.timeSinceResume));
    },
    timeSinceResume(): number {
      if (this.clientResumedAt) {
        return Math.max(0, this.timeNow.valueOf() - this.clientResumedAt);
      }
      if (this.timeResumed > 0) {
        const serverAdjustedNow = this.timeNow.valueOf() - this.serverClockOffset;
        return Math.max(0, serverAdjustedNow - this.timeResumed);
      }
      return 0;
    },
    timeResumed(): number {
      return (!!this.startedAt && !this.isPaused && !!this.treatment.resumed_at)
          ? this.$dayjs(this.treatment.resumed_at).utc().valueOf()
          : 0;
    },
    timeElapsedFormatted(): any {
      const t = this.$dayjs.duration(this.timeElapsed).asMilliseconds();
      return this.$dayjs.utc(t).format(t >= 3600000 ? 'hh:mm' : 'mm:ss');
    },
    timeAllocated(): any {
      return this.localAllocatedTime ?? this.initialAllocatedTime;
    },
    timeAllocatedFormatted(): any {
      const t = this.$dayjs.duration(this.timeAllocated).asMilliseconds();

      return this.$dayjs.utc(t).format(t >= 3600000 ? 'hh:mm' : 'mm:ss');
    },
    initialsBackgroundColor() { return this.initialsColor || randomColor(); },
    playIcon() { return this.isPaused ? mdiMotionPlayOutline : mdiPlay; },
    filter() {
      return this.$store.state.filter;
    },
    isDisabled() {

      if (!!this.filter) {
        return this.treatment.doctor.uuid !== this.filter;
      }

      return this.appointment.treatments
          .filter(({ uuid }) => uuid !== this.treatment.uuid)
          .some(({ status, uuid }) => {
            return ['paused', 'processing'].includes(status);
          });
    },
  },
  watch: {
    timeNow() { this.emitTime(); },
    isOverTime: 'playWarning',
    'treatment.resumed_at'() {
      this.clientResumedAt = null;
    },
    'treatment.status'() {
      this.clientResumedAt = null;
    },
  },
  methods: {
    ...mapActions([
      'LOAD_DASHBOARD',
      'HANDLE_RESET_TREATMENT',
      'HANDLE_PLAY_TREATMENT',
      'HANDLE_PAUSE_TREATMENT',
      'HANDLE_COMPLETE_TREATMENT',
      'HANDLE_UPDATE_ALLOCATED_TIME_TREATMENT',
    ]),
    playWarning() {
      let treatmentKey = null;
      this.appointment.treatments.map((t, key) => {
        if (t.uuid === this.treatment.uuid) {
          treatmentKey = key;
        }
      });
      const isPlayedWarning = this.appointment.treatments[treatmentKey].playedWarning;
      const activeStatus = [ 'processing', 'pending' ];

      if (this.isOverTime && !isPlayedWarning && activeStatus.includes(this.treatment.status)) {
        const dangerAudio = new Audio(require('@/assets/audio/danger.mp3'));
        dangerAudio.play();
        this.appointment.treatments[treatmentKey].playedWarning = true;
      }
    },

    async startTreatment(event?: any): Promise<void> {
      console.log('*1');
      const condition = this.appointment.treatments
          .filter(({ uuid }) => uuid !== this.treatment.uuid)
          .some(({ started_at, ended_at }) => (!!started_at && !ended_at));

      console.log('*2');
      if (!condition) {
        const {
          $store: { state: { auth: { user: { uuid: user }}}},
          appointmentId,
          id: treatment,
          roomId: room,
          startedAt,
        } = this;
        const params = { user };

        console.log('*3');
        await Promise.all([
          this.HANDLE_PLAY_TREATMENT({
            appointmentId, params: {
            ...params,
            treatment,
            ...(!!startedAt ? {} : {
              room,
            }),
            },
          }),
        ]);

      }
      console.log('*4');

    },

    emitTime() {
      const time = this.timeElapsed;
      this.$eventBus.emit(`room-${this.roomId}-ticker`, { time });
    },

    openDialog() {
      store.commit('toggleTreatment', { openedTreatmentUUID: this.treatment.uuid });
    },

    closeDialog() {
      store.commit('toggleTreatment', { openedTreatmentUUID: null });
    },

    async handlePlay(): Promise<void> {
      if (!this.isDisabled) {
        this.clientResumedAt = Date.now();
        await this.startTreatment();
      }
    },

    async handlePause(): Promise<void> {
      this.clientResumedAt = null;

      if (!this.canInteractWithTreatment || !this.isProcessing || this.isDisabled) {
        return null;
      }

      const {
        $store: {state: {auth: {user: {uuid: user}}}},
        appointmentId,
        id: treatment,
      } = this;
      const params = {user};

      await this.HANDLE_PAUSE_TREATMENT({
        appointmentId, params: {
          ...params,
          time_elapsed: (this.timeElapsed ? (this.timeElapsed / 1000) : 0).toString(),
          treatment,
        },
      });
    },

    async handleCompleteTreatment(): Promise<void> {
      const {
        $store: { state: { auth: { user: { uuid: user }}}},
        appointmentId,
        id: treatment,
      } = this;

      await this.HANDLE_COMPLETE_TREATMENT({ appointmentId, params: {
        time_elapsed: (this.timeElapsed ? (this.timeElapsed / 1000) : 0).toString(),
        treatment,
        user,
      }});
    },

    async handleResetTreatment(): Promise<void> {
      const {
        $store: { state: { auth: { user: { uuid: user }}}},
        appointmentId,
        id: treatment,
      } = this;

      await this.HANDLE_RESET_TREATMENT({ appointmentId, params: {
        treatment,
        user,
      }});

      this.emitTime();
    },

    handleUpdatetimeAllocated(payload: number): void {
      this.localAllocatedTime = this.timeAllocated + (payload * 1000);

      if (this.allocatedTimeDebounceTimer) {
        clearTimeout(this.allocatedTimeDebounceTimer);
      }

      const { appointmentId, id: treatment } = this;
      const duration = (this.localAllocatedTime / 1000).toString();

      this.allocatedTimeDebounceTimer = setTimeout(async () => {
        await this.HANDLE_UPDATE_ALLOCATED_TIME_TREATMENT({ appointmentId, params: {
          treatment,
          duration,
        }});
        this.localAllocatedTime = null;
        this.allocatedTimeDebounceTimer = null;
      }, 800);
    },

    start(): void {
      if (!this.interval) { this.interval = setInterval(() => {
        this.count++;
        if (this.count > 15) { this.openDialog(); }
      }, 30);
      }
    },

    stop(): void {
      clearInterval(this.interval);

      if (this.count > 0 && this.count < 15 && this.interactWithBoard) {
        if (!this.startedAt || this.isPaused) {
          this.handlePlay();
        } else {
          this.handlePause();
        }
      }

      this.interval = undefined;
      this.count = 0;
    },

  },
});
</script>

<style scoped>
.treatment-card {
  flex: none;
  width: 108px;
  height: 60px;
  cursor: pointer;
  @apply p-1 flex flex-col justify-between relative;
}

.unSelectedByDoctor {
  opacity: 0.3;
}

.greenCard {
  background-color: #a0fcd1;
  border: #a0fcd1 solid 1px;
}

.yellowCard {
  background-color: #fbe04b;
  border: #fbe04b solid 1px;
}

.redCard {
  background-color: #e83123;
  border: #e83123 solid 1px;
  color: #efefef;
}

.redCard div {
  color: #efefef !important;
}

.whiteCard {
  background-color: #eaeaea;
  border: #eaeaea solid 1px;
}

.treatment-time {
  letter-spacing: -0.5px !important
}

.tc-initials {
  border-bottom-right-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
  border-top-left-radius: 0 !important;
}

.comment-icon {
  position: absolute;
  right: 2px;
  top: 15px;
}
</style>
